/**
 * Visitor-username display helpers for the admin dashboard.
 *
 * Anonymous chat visitors land in `question_answer.username` with the
 * `anon_<uuidv4>` value minted by the server-side visitorIdentity
 * middleware (see apps/cucs-advising/RATE_LIMITS.md). Rendering the full
 * UUID makes admin tables a wall of noise — we instead show
 * "Anonymous #xxxxxxxx" where the suffix is the first 8 hex chars of
 * the UUID. That's enough to spot the same visitor across multiple
 * questions without being unreadable.
 *
 * Signed-in admins (password auth) appear with their real username
 * untouched.
 */

/** Prefix server-side visitor cookie values start with. */
export const ANON_USERNAME_PREFIX = 'anon_';

/** Number of hex chars from the UUID to keep in the display suffix —
 * enough to be visually distinct across thousands of visitors without
 * crowding the column. */
const ANON_DISPLAY_SUFFIX_LEN = 8;

export interface UsernameDisplay {
  /** What to render in the admin UI ("alice", "Anonymous #abc12345", "-"). */
  display: string;
  /** True if the underlying value was an `anon_<uuid>` cookie identity. */
  isAnonymous: boolean;
}

/**
 * Format a `question_answer.username` value for display in the admin
 * dashboard.
 *
 * - `undefined` / `null` / `''` → `{ display: '-', isAnonymous: false }`
 * - `anon_550e8400-...` → `{ display: 'Anonymous #550e8400', isAnonymous: true }`
 * - any other string → `{ display: <as-is>, isAnonymous: false }`
 */
export function formatUsername(rawUsername: string | undefined | null): UsernameDisplay {
  if (!rawUsername) return { display: '-', isAnonymous: false };
  if (rawUsername.startsWith(ANON_USERNAME_PREFIX)) {
    const suffix = rawUsername.slice(
      ANON_USERNAME_PREFIX.length,
      ANON_USERNAME_PREFIX.length + ANON_DISPLAY_SUFFIX_LEN
    );
    return { display: `Anonymous #${suffix}`, isAnonymous: true };
  }
  return { display: rawUsername, isAnonymous: false };
}

/** True iff the username belongs to an anonymous chat visitor (not an
 * authenticated admin). */
export function isAnonymousUsername(rawUsername: string | undefined | null): boolean {
  return !!rawUsername && rawUsername.startsWith(ANON_USERNAME_PREFIX);
}
